import { shuffle, nextRandom } from '../rng/rng';
import { cardDefinitions, heroGroupKeys } from '../cards/cardDefinitions.generated';
import { copyCounts } from '../cards/copyCounts.generated';
import { getClusterRoles } from '../ai/heuristics/synergyClusters';
import {
  VILLAGE_BASIC_STACK_IDS, HERO_STACKS_PER_GAME,
  WEAPON_STACKS_PER_GAME, ITEM_STACKS_PER_GAME, SPELL_STACKS_PER_GAME, VILLAGER_STACKS_PER_GAME,
  HIDDEN_SETS, RANDOMIZED_HERO_STACKS_PER_GAME, RANDOMIZED_VILLAGE_STACKS_PER_GAME, RANDOMIZED_BASIC_VILLAGE_STACK_SIZE,
} from '../constants';

// See constants.js's HIDDEN_SETS - a hero group's set lives on its own
// level-1 card, not on the group key itself.
const SELECTABLE_HERO_GROUP_KEYS = heroGroupKeys.filter((group) => !HIDDEN_SETS.has(
  cardDefinitions.find((d) => d.id === `hero-${group}-l1`).set,
));

// One randomized stack per empty board slot, per village-row category
// (matches the physical board layout exactly - see rulebook board diagram).
const VILLAGE_CATEGORY_SLOTS = [
  { trait: 'Weapon', count: WEAPON_STACKS_PER_GAME },
  { trait: 'Item', count: ITEM_STACKS_PER_GAME },
  { trait: 'Spell', count: SPELL_STACKS_PER_GAME },
  { trait: 'Villager', count: VILLAGER_STACKS_PER_GAME },
];

// A hero group's full stack: level1..level3, 6/4/2 copies per level (so
// lower levels are always exhausted first) - shared by buildVillage and
// buildRandomizedVillage below.
function buildHeroStack(group, makeInstance) {
  const stack = [];
  for (const level of [1, 2, 3]) {
    const defId = `hero-${group}-l${level}`;
    const count = copyCounts[defId].total;
    for (let i = 0; i < count; i += 1) stack.push(makeInstance(defId));
  }
  return stack;
}

// Curse deck (every curse instance, shuffled) and familiar deck (one of
// each familiar, shuffled) - identical for both village-build modes.
function buildCurseAndFamiliarDecks(makeInstance, rngState) {
  let state = rngState;
  const curseDefs = cardDefinitions.filter((d) => d.type === 'curse');
  const curseInstances = [];
  for (const def of curseDefs) {
    const count = copyCounts[def.id].total;
    for (let i = 0; i < count; i += 1) curseInstances.push(makeInstance(def.id));
  }
  const [curseDeck, afterCurseShuffle] = shuffle(curseInstances, state);
  state = afterCurseShuffle;

  const familiarDefs = cardDefinitions.filter((d) => d.type === 'familiar');
  const familiarInstances = familiarDefs.map((def) => makeInstance(def.id));
  const [familiarDeck, afterFamiliarShuffle] = shuffle(familiarInstances, state);
  state = afterFamiliarShuffle;

  return [{ curseDeck, familiarDeck }, state];
}

// Matt: "the total value of all the monster levels... I usually target 10
// total" (see dungeon/dungeonDeckSetup.js's own house-rule handler) is a
// separate rule from this one - the only card known to depend on a Regular
// stack actually being present on the board is Royal Summons (grep-
// confirmed: `state.village.basicStacks['basic-regular']`, the only
// direct read of that key outside villageSetup.js itself). If a future
// card ever reads that key too, add its id here.
const REGULAR_STACK_DEPENDENCIES = new Set(['village-royal-summons']);

// Matt: "It would be fun if you could have synergies up here more often in
// the randomized card draw... maybe that could be a house rule checkbox...
// full random versus synergy preferred." Follow-up, when asked whether the
// AI-tournament synergy work (synergyClusters.js's CLUSTERS, empirically
// tuned via self-play round-robins - see scripts/rosters/) could be reused
// here: "isn't that stuff useful here." Yes - getClusterRoles exposes the
// same cluster table as a pure per-card lookup, reused below to bias this
// draw toward cards that share a cluster with ones already selected,
// instead of inventing a second, ungrounded definition of "synergy."
//
// SYNERGY_BOOST/SYNERGY_PARTICIPANT_CAP are a nudge, not a guarantee - a
// card touching 0 clusters always keeps weight 1 (still fully eligible,
// most cards in the game don't participate in any named cluster), and the
// cap keeps one already-huge cluster from crowding out everything else
// once a few of its cards are in. Deliberately much stronger than
// villageHeuristics.js's own buy-time synergyScore nudge (3-30 magnitude
// against a cardValue baseline) - here there's no risk of a runaway
// self-reinforcing buy loop (see synergyClusters.js's own comment on that
// regression), just which cards land on the board at all, so a firmer
// clustering effect is safe and is the actual point of the house rule.
const SYNERGY_BOOST = 3;
const SYNERGY_PARTICIPANT_CAP = 4;

// pickedClusterCounts: Map<clusterId, count> - how many cards ALREADY
// selected this village (heroes and village cards share one map, so a
// selected hero can raise a village card's weight and vice versa) touch
// each cluster, in either role (member or scaler treated the same for
// weighting purposes - either one signals "this cluster is present").
function synergyWeight(defId, pickedClusterCounts) {
  let boost = 0;
  for (const { clusterId } of getClusterRoles(defId)) {
    boost += Math.min(pickedClusterCounts.get(clusterId) || 0, SYNERGY_PARTICIPANT_CAP);
  }
  return 1 + boost * SYNERGY_BOOST;
}

function recordClusterParticipation(defIds, pickedClusterCounts) {
  for (const defId of defIds) {
    for (const { clusterId } of getClusterRoles(defId)) {
      pickedClusterCounts.set(clusterId, (pickedClusterCounts.get(clusterId) || 0) + 1);
    }
  }
}

// Weighted draw without replacement, seeded (same nextRandom primitive
// shuffle() itself uses, so this stays deterministic/replayable). `toDefIds`
// maps one candidate to the card id(s) whose cluster membership should
// count toward its own weight AND get recorded once picked - a single id
// for a village card, or a whole hero group's l1..l4 ids at once (picking
// "clan" should weigh in Clan King's scaler role immediately, not only
// once some later step "draws" l4 specifically - the group is one atomic
// pick). Returns [pickedCandidates, nextRngState].
function weightedPick(candidates, count, toDefIds, pickedClusterCounts, rngState) {
  const remaining = candidates.slice();
  let state = rngState;
  const picked = [];
  for (let i = 0; i < count && remaining.length; i += 1) {
    const weights = remaining.map((candidate) => Math.max(
      ...toDefIds(candidate).map((defId) => synergyWeight(defId, pickedClusterCounts)),
    ));
    const total = weights.reduce((sum, w) => sum + w, 0);
    const [rand, nextState] = nextRandom(state);
    state = nextState;
    let threshold = rand * total;
    let idx = weights.length - 1;
    for (let w = 0; w < weights.length; w += 1) {
      threshold -= weights[w];
      if (threshold <= 0) { idx = w; break; }
    }
    const [chosen] = remaining.splice(idx, 1);
    picked.push(chosen);
    recordClusterParticipation(toDefIds(chosen), pickedClusterCounts);
  }
  return [picked, state];
}

// Matt's house rule: "I always remove the regular, longspear, and torch
// stacks and replace them so that I have 5 random hero stacks and 13
// random village stacks. I don't bother sorting them by type it's just
// fully random. ... The longspear and the torch can be included as normal
// village cards and will occasionally be included randomly like any other
// card." Regular itself is excluded from the pool - "there are cards
// (like the royal summons) which need a stack of regulars. In that case,
// add the regulars as a 6th hero stack" - handled by leaving the Regular
// stack (when needed) at its normal `basicStacks['basic-regular']` data
// location (so Royal Summons's own handler needs zero changes) and having
// VillageBoard.vue display it grouped with the hero stacks in this mode.
// A hero group's own defId set for cluster-weighting purposes - every level
// that actually exists for it (most groups are l1-l3; some also reach l4
// via leveling beyond l3, see village/leveling.js's isMaxLevel - either
// way, selecting the GROUP brings its whole combo package into the game,
// so all of it should count, not just whichever levels buildHeroStack
// physically stacks at setup time).
function heroGroupDefIds(group) {
  return [1, 2, 3, 4]
    .map((level) => `hero-${group}-l${level}`)
    .filter((defId) => cardDefinitions.some((d) => d.id === defId));
}

// Returns [village, nextRngState], same shape as buildVillage.
export function buildRandomizedVillage({
  playerCount, makeInstance, rngState, synergyPreferred = false,
}) {
  let state = rngState;
  const pickedClusterCounts = new Map();

  let selectedHeroGroups;
  if (synergyPreferred) {
    const [shuffledHeroGroups, afterHeroShuffle] = shuffle(SELECTABLE_HERO_GROUP_KEYS, state);
    state = afterHeroShuffle;
    const [picked, afterPick] = weightedPick(
      shuffledHeroGroups, RANDOMIZED_HERO_STACKS_PER_GAME, heroGroupDefIds, pickedClusterCounts, state,
    );
    selectedHeroGroups = picked;
    state = afterPick;
  } else {
    const [shuffledHeroGroups, afterHeroShuffle] = shuffle(SELECTABLE_HERO_GROUP_KEYS, state);
    state = afterHeroShuffle;
    selectedHeroGroups = shuffledHeroGroups.slice(0, RANDOMIZED_HERO_STACKS_PER_GAME);
  }
  const heroStacks = {};
  for (const group of selectedHeroGroups) heroStacks[group] = buildHeroStack(group, makeInstance);

  const villagePoolDefIds = cardDefinitions
    .filter((d) => (d.type === 'village' || d.id === 'basic-longspear' || d.id === 'basic-torch') && !HIDDEN_SETS.has(d.set))
    .map((d) => d.id);
  let selectedVillageDefIds;
  if (synergyPreferred) {
    const [shuffledVillagePool, afterVillageShuffle] = shuffle(villagePoolDefIds, state);
    state = afterVillageShuffle;
    const [picked, afterPick] = weightedPick(
      shuffledVillagePool, RANDOMIZED_VILLAGE_STACKS_PER_GAME, (defId) => [defId], pickedClusterCounts, state,
    );
    selectedVillageDefIds = picked;
    state = afterPick;
  } else {
    const [shuffledVillagePool, afterVillageShuffle] = shuffle(villagePoolDefIds, state);
    state = afterVillageShuffle;
    selectedVillageDefIds = shuffledVillagePool.slice(0, RANDOMIZED_VILLAGE_STACKS_PER_GAME);
  }
  const villageStacks = {};
  for (const defId of selectedVillageDefIds) {
    // basic-longspear/basic-torch's own copyCounts.total (25) is sized for
    // their ALWAYS-present basic-stack role, not a normal village-stack
    // slot - see RANDOMIZED_BASIC_VILLAGE_STACK_SIZE's own comment.
    const count = defId === 'basic-longspear' || defId === 'basic-torch'
      ? RANDOMIZED_BASIC_VILLAGE_STACK_SIZE
      : copyCounts[defId].total;
    villageStacks[defId] = Array.from({ length: count }, () => makeInstance(defId));
  }

  const basicStacks = {};
  if (selectedVillageDefIds.some((id) => REGULAR_STACK_DEPENDENCIES.has(id))) {
    const regularBoxMax = copyCounts['basic-regular'].total;
    const regularCount = Math.min(3 * playerCount, regularBoxMax);
    basicStacks['basic-regular'] = Array.from({ length: regularCount }, () => makeInstance('basic-regular'));
  }

  const [{ curseDeck, familiarDeck }, afterDecks] = buildCurseAndFamiliarDecks(makeInstance, state);
  state = afterDecks;

  const village = {
    heroStacks, villageStacks, basicStacks, curseDeck, familiarDeck,
  };
  return [village, state];
}

// Builds the village: 4 randomly-selected hero stacks (each ordered level1..level3,
// 6/4/2 copies per level so lower levels are always exhausted first), one
// randomly-selected stack per empty board slot in each village-row category
// (3 Weapon/2 Item/3 Spell/3 Villager), and 3 basic stacks (Regular /
// Longspear / Torch) sized at 3 copies per player, capped at the box maximum.
// Returns [village, nextRngState].
export function buildVillage({ playerCount, makeInstance, rngState }) {
  let state = rngState;

  const [shuffledHeroGroups, afterHeroShuffle] = shuffle(SELECTABLE_HERO_GROUP_KEYS, state);
  state = afterHeroShuffle;
  const selectedHeroGroups = shuffledHeroGroups.slice(0, HERO_STACKS_PER_GAME);
  const heroStacks = {};
  for (const group of selectedHeroGroups) heroStacks[group] = buildHeroStack(group, makeInstance);

  const villageStacks = {};
  for (const { trait, count: slotCount } of VILLAGE_CATEGORY_SLOTS) {
    const categoryDefIds = cardDefinitions
      .filter((d) => d.type === 'village' && (d.traits || []).includes(trait) && !HIDDEN_SETS.has(d.set))
      .map((d) => d.id);
    const [shuffledCategoryDefIds, afterCategoryShuffle] = shuffle(categoryDefIds, state);
    state = afterCategoryShuffle;
    for (const defId of shuffledCategoryDefIds.slice(0, slotCount)) {
      const count = copyCounts[defId].total;
      villageStacks[defId] = Array.from({ length: count }, () => makeInstance(defId));
    }
  }

  const basicStacks = {};
  for (const defId of VILLAGE_BASIC_STACK_IDS) {
    if (defId === 'basic-regular') continue; // Regular is bought from its own hero-like slot, not a village stack
    const boxMax = copyCounts[defId].total;
    const count = Math.min(3 * playerCount, boxMax);
    basicStacks[defId] = Array.from({ length: count }, () => makeInstance(defId));
  }
  // Regular gets the same "3 per player" sizing rule as the other basics.
  const regularBoxMax = copyCounts['basic-regular'].total;
  const regularCount = Math.min(3 * playerCount, regularBoxMax);
  const regularStack = Array.from({ length: regularCount }, () => makeInstance('basic-regular'));

  const [{ curseDeck, familiarDeck }, afterDecks] = buildCurseAndFamiliarDecks(makeInstance, state);
  state = afterDecks;

  const village = {
    heroStacks,
    villageStacks,
    basicStacks: { ...basicStacks, 'basic-regular': regularStack },
    curseDeck,
    familiarDeck,
  };
  return [village, state];
}
